/* ===================================================================
   Dashboard screen -- PIXEL-FAITHFUL rebuild of the Claude Design mockup
   ("Rhody Reviews.dc.html", DASHBOARD screen). Hopper + Brad, 2026-07-02.

   This is a deliberate, literal reproduction of the mockup's visual output:
   the advisor line, setup card, KPI row (with 7-week trend sparklines),
   the "Reply to N reviews" hero card, review-activity heatmap, customer
   words, momentum/streak, rating-over-time bars, "where reviews come from",
   needs-attention, and this-month's-requests -- laid out and styled exactly
   as in the mockup (see styles/dashboard-faithful.css, values lifted verbatim).

   DATA WIRING PRESERVED. Every live hook and product rule from the prior
   Dashboard is kept:
     - useReviews / useDashboardSummary  -> KPIs, hero cards, sources, chart
     - useNotifications                  -> needs-attention
     - useRequestsSummary                -> this-month funnel
     - useActivity                       -> heatmap + momentum/streak
     - useOutboxActions                  -> Approve / Skip on hero cards
     - useMe / useBusiness / useUpdateBusiness -> greeting + goal
   HARD PRODUCT RULES intact: AI drafts never auto-post (only the explicit
   "Approve & post" click posts); Google-only sourcing; human approves every
   reply; no gating/NPS.

   NO-FABRICATION PASS (Brad, 2026-07-06, audit C1): every mock/demo fallback
   the reskin re-imported is GONE. When the backend cannot supply a panel's
   data the panel shows an honest zero / "-" / empty state -- NEVER an
   invented number. A zero-data tenant (no reviews yet) is routed to the
   honest FirstRun day-0 screen instead of this dashboard. Do not re-add
   sample values here; unlabeled sample data on a prod screen is a hard-rule
   violation ("never fabricate stats").

   APPEARANCE: locked to outdoor high-contrast (Steve, 2026-08-06). The
   mockup's Light / Dark / Outdoor switcher is retired -- see
   theme/lockedTheme.ts.
   =================================================================== */
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReviews,
  useDashboardSummary,
  useNotifications,
  useRequestsSummary,
  useRequests,
  useActivity,
  useOutboxActions,
  useMe,
} from '../hooks';
import { relativeTime, daypartGreeting } from '../lib/display';
import { FinishSetupCard } from '../components/overlays/FinishSetupCard';
import { ReactivationPush } from '../components/overlays/ReactivationPush';
import { useAdminView } from '../admin/adminViewContext';
import type { AnalyticsComparison, ComparisonMetric } from '../lib/api/core';
import '../styles/dashboard-faithful.css';

/* ---- small faithful SVG helpers (mockup _stars / _spark / _heatEl / _barsEl) ---- */

// mockup _stars(n, size): 5 stars, filled #fbbc04 else #dadce0.
function Stars({ n, size }: { n: number; size: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} style={{ color: i < n ? '#fbbc04' : '#80868b', fontSize: size, lineHeight: 1 }}>
          ★
        </span>
      ))}
    </span>
  );
}

// mockup _spark(points, color): 68x22 line + end dot.
function Spark({ points, color }: { points: number[]; color: string }) {
  const w = 68,
    h = 22;
  if (points.length < 2) return <svg width={w} height={h} />;
  const min = Math.min(...points),
    max = Math.max(...points);
  const rng = max - min || 1;
  const pts = points.map((v, i) => [
    (i / (points.length - 1)) * w,
    h - 2 - ((v - min) / rng) * (h - 4),
  ]);
  const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }}>
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.6} fill={color} />
    </svg>
  );
}

// mockup _barsEl(): 12 vertical bars, min 3.8 max 5, gradient fill.
function RatingBars({ values }: { values: number[] }) {
  const max = 5,
    min = 3.8;
  // Truthful x-axis: derive the three month labels from the REAL weeks the bars
  // cover (one bar per week, ending this week), instead of the old hardcoded
  // "Apr / May / Jun" that mislabeled real data whenever it wasn't spring.
  const n = values.length;
  const monthAtBar = (idx: number): string => {
    const d = new Date();
    d.setDate(d.getDate() - (n - 1 - idx) * 7);
    return d.toLocaleString('en-US', { month: 'short' });
  };
  const axis =
    n > 0
      ? [monthAtBar(0), monthAtBar(Math.floor((n - 1) / 2)), monthAtBar(n - 1)]
      : [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110 }}>
        {values.map((v, i) => {
          const h = ((v - min) / (max - min)) * 100;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: '100%',
                  maxWidth: 18,
                  height: Math.max(8, h),
                  background: 'linear-gradient(180deg,#4285F4,#1a73e8)',
                  borderRadius: '5px 5px 3px 3px',
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#41454a' }}>
        {axis.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>
    </div>
  );
}

/* mockup _heatEl(): 26-week contribution grid. Reproduced exactly (same cell
   size, gaps, colors, month row, weekday column, legend). `weeks` is derived
   from live activity when available, else the mockup's seeded pattern. */
type HeatWeek = { label: string; days: number[] };
function Heatmap({ weeks, total }: { weeks: HeatWeek[]; total: number }) {
  const colors = ['#eef1f4', '#c6ddfb', '#7fb0f8', '#1a73e8'];
  const cell = 12,
    gap = 3;
  const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
  return (
    <div>
      <div style={{ display: 'flex', gap, marginLeft: 30, marginBottom: 5, height: 13 }}>
        {weeks.map((wk, wi) => (
          <div
            key={wi}
            style={{ width: cell, fontSize: 12, fontWeight: 700, color: '#41454a', whiteSpace: 'nowrap', overflow: 'visible' }}
          >
            {wk.label}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap, marginRight: 6, width: 24 }}>
          {dayLabels.map((lbl, di) => (
            <div key={di} style={{ height: cell, fontSize: 12, fontWeight: 700, color: '#41454a', lineHeight: `${cell}px`, textAlign: 'right' }}>
              {lbl}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap }}>
          {weeks.map((wk, wi) => (
            <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap }}>
              {wk.days.map((v, di) => (
                <div
                  key={di}
                  title={v >= 0 ? `${v} ${v === 1 ? 'review' : 'reviews'}` : ''}
                  style={{ width: cell, height: cell, borderRadius: 3, background: v < 0 ? 'transparent' : colors[v] }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, fontSize: 14, fontWeight: 600, color: '#41454a' }}>
        <span>
          <strong style={{ color: '#000000', fontWeight: 800 }}>{total}</strong> reviews in the last 6 months
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Less
          {colors.map((c, i) => (
            <div key={i} style={{ width: cell, height: cell, borderRadius: 3, background: c }} />
          ))}
          More
        </div>
      </div>
    </div>
  );
}

/* Map live activity days -> the 26-week heat grid shape, bucketed 0-3.
   With no activity data the grid renders honestly EMPTY (all zero cells) --
   the old seeded demo pattern is gone (audit C1: never fabricate). */
function heatFromActivity(days: { date: string; count: number }[]): { weeks: HeatWeek[]; total: number } {
  const byDate = new Map(days.map((d) => [d.date, d.count]));
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - (25 * 7 + today.getDay()));
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const weeks: HeatWeek[] = [];
  let total = 0,
    lastMonth = -1;
  const bucket = (n: number) => (n <= 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : 3);
  for (let w = 0; w < 26; w++) {
    const wkDays: number[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() + w * 7);
    const m = weekStart.getMonth();
    let label = '';
    if (m !== lastMonth) {
      label = monthNames[m];
      lastMonth = m;
    }
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(weekStart);
      cellDate.setDate(cellDate.getDate() + d);
      if (cellDate > today) {
        wkDays.push(-1);
        continue;
      }
      const key = cellDate.toISOString().slice(0, 10);
      const n = byDate.get(key) ?? 0;
      total += n;
      wkDays.push(bucket(n));
    }
    weeks.push({ label, days: wkDays });
  }
  return { weeks, total };
}

// next milestone (mockup _nextMilestone).
function nextMilestone(n: number): number {
  const steps = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
  let m = steps.find((sv) => sv > n && n / sv <= 0.9);
  if (!m) m = steps.find((sv) => sv > n) || Math.ceil((n + 1) / 1000) * 1000;
  return m;
}

/* comparison caption (kept from prior Dashboard, drives the KPI delta text). */
type KpiKind = 'rating' | 'count' | 'percent';
function comparisonCaption(
  m: ComparisonMetric | undefined,
  kind: KpiKind,
  fallback: string,
): { dir: 'up' | 'down' | 'flat'; caption: string } {
  if (!m || m.previous == null || m.delta == null) return { dir: 'flat', caption: fallback };
  const d = m.delta;
  const sign = d > 0 ? '+' : d < 0 ? '-' : '';
  const mag = Math.abs(d);
  const absAmount =
    kind === 'rating' ? mag.toFixed(1) : kind === 'percent' ? `${Math.round(mag * 100)} pts` : String(Math.round(mag));
  // V11: a percentage off a near-zero base is meaningless ("1020% vs last 30
  // days"). Only show a percentage when the previous period was a meaningful
  // base AND the change is within a sane range; otherwise show the absolute
  // change, which is always honest.
  if (
    m.pct_change != null &&
    typeof m.previous === 'number' &&
    Math.abs(m.previous) >= (kind === 'count' ? 5 : 1) &&
    Math.abs(m.pct_change) <= 3 /* <=300% */
  ) {
    const pct = Math.abs(Math.round(m.pct_change * 100));
    return { dir: m.dir, caption: `${pct}% vs last 30 days` };
  }
  return { dir: m.dir, caption: `${sign}${absAmount} vs last 30 days` };
}

interface TrendSeriesPoint { value: number | null; label: string }
interface TrendSummary {
  avg_rating_trend?: { series?: TrendSeriesPoint[]; meta?: { overall_avg?: number | null } };
  review_count_by_week?: { series?: TrendSeriesPoint[] };
}

/* icon shorthands ------------------------------------------------------- */
const svg = (props: React.SVGProps<SVGSVGElement>, children: ReactNode) => (
  <svg viewBox="0 0 24 24" {...props}>
    {children}
  </svg>
);
const GoogleG = ({ s = 13 }: { s?: number }) =>
  svg({ width: s, height: s, fill: '#4285F4' }, <path d="M12 11v2.6h4.3c-.2 1.1-1.4 3.2-4.3 3.2a4.8 4.8 0 0 1 0-9.6c1.5 0 2.5.6 3 1.1l2-2A7.5 7.5 0 1 0 12 19.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2z" />);
const Bolt = ({ s = 20, stroke = '#fff' }: { s?: number; stroke?: string }) =>
  svg({ width: s, height: s, fill: 'none', stroke, strokeWidth: 2 }, <path d="M13 2L4 14h6l-1 8 9-12h-6z" />);
const CaretR = ({ s = 15, stroke = '#c3c8ce' }: { s?: number; stroke?: string }) =>
  svg({ width: s, height: s, fill: 'none', stroke, strokeWidth: 2.2 }, <path d="M9 6l6 6-6 6" />);
const TrendUp = () => svg({ width: 13, height: 13, fill: 'none', stroke: 'currentColor', strokeWidth: 3 }, <path d="M6 15l6-6 6 6" />);

const STAR = (
  <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
);

const AVATAR_COLORS = ['#7c4dff', '#00897b', '#1a73e8', '#c0562b', '#2e7d32', '#6a1b9a', '#1565c0', '#00838f'];
function avatarColor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

/* §9 EMPTY-STATE -> LIVE FLIP. Shown to a zero-review owner who HAS already
   sent their first request (requests_sent >= 1): the moment they activate, the
   dashboard flips from the checklist to live counter tiles so they see real
   funnel motion before the first review even lands. Fully derived from real
   data; no fabricated numbers. Tokens only -- reuses the existing rhf-* classes
   so it inherits theme + no new hardcoded colors. */
function EmptyStateLiveTiles({
  requestsSent,
  reviewsReceived,
  greetingName,
  onSendMore,
}: {
  requestsSent: number;
  reviewsReceived: number;
  greetingName: string;
  onSendMore: () => void;
}) {
  const pending = Math.max(0, requestsSent - reviewsReceived);
  const tile = (label: string, value: number, caption: string) => (
    <div className="rhf-kpi" style={{ cursor: 'default' }}>
      <div className="rhf-kpi__head">
        <div className="rhf-kpi__label">{label}</div>
      </div>
      <div className="rhf-kpi__big" style={{ marginTop: 8 }}>{value.toLocaleString()}</div>
      <div className="rhf-kpi__row2">
        <span className="rhf-kpi__delta rhf-kpi__delta--flat">{caption}</span>
      </div>
    </div>
  );
  return (
    <div className="rhf-dash">
      <div style={{ marginBottom: 14 }}>
        <div className="rhf-mgreet__h">{daypartGreeting()}{greetingName ? `, ${greetingName}` : ''}</div>
        <div className="rhf-mgreet__s">Your first requests are on their way. Reviews will land here as customers respond.</div>
      </div>

      <div className="rhf-advisor">
        <div className="rhf-advisor__icon" data-keepcolor>
          <Bolt />
        </div>
        <div className="rhf-advisor__text">
          Nice work. Your review engine is live. Keep sending requests to keep the 5-stars coming.
        </div>
      </div>

      {/* POST-ACTIVATION reactivation push (spec): a dismissible nudge, not a
          setup-checklist row. Self-gates on activated + not-yet-reactivated. */}
      <ReactivationPush />

      <div className="rhf-kpirow">
        {tile('REQUESTS SENT', requestsSent, 'Review asks from your account')}
        {tile('AWAITING REVIEW', pending, 'Awaiting a review')}
        {tile('REVIEWS RECEIVED', reviewsReceived, 'Live count as they arrive')}
      </div>

      <div className="rhf-panel rhf-panel--pad" style={{ marginTop: 16, textAlign: 'center' }}>
        <div className="rhf-panel__title" style={{ marginBottom: 6 }}>Your first reviews are on the way</div>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--muted)', margin: '0 auto 16px', maxWidth: 440 }}>
          As soon as a customer leaves a review, your full dashboard - ratings, trends, and
          AI-drafted replies - lights up right here.
        </p>
        <button
          className="rhf-btn rhf-btn--primary"
          style={{ padding: '14px 26px', fontSize: 17 }}
          onClick={onSendMore}
        >
          Send more requests
        </button>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const readOnly = useAdminView() !== null;

  // Launch QC 2026-07-06: in the admin READ-ONLY tenant view, KPI tiles + hero
  // buttons must NOT navigate to absolute app-shell routes (/reviews, /requests,
  // /automations) -- that bounces the operator OUT of the read-only view into
  // their OWN write-enabled screens. `go` is inert in read-only, so the tiles
  // are non-interactive there (matching the already-gated write controls).
  const go = (path: string) => {
    if (readOnly) return;
    navigate(path);
  };

  // ---- appearance is LOCKED app-wide to outdoor high-contrast (AppShell
  // stamps the constant). The Dashboard owns no theme state and no toggle. ----

  // ---- LIVE data (unchanged wiring) ----
  const { data: reviewsData } = useReviews({ limit: 500 });
  const { data: summary } = useDashboardSummary();
  const reviews = useMemo(() => reviewsData?.reviews ?? [], [reviewsData]);
  const totalReviews = reviewsData?.count ?? reviews.length;
  const { approve, skip } = useOutboxActions();
  const { data: me } = useMe();

  const draftIdByReview = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reviews) if (r.draft_id) m.set(r.review_id, r.draft_id);
    return m;
  }, [reviews]);

  const comparison = (summary as { comparison?: AnalyticsComparison } | undefined)?.comparison;
  const cmpRating = useMemo(() => comparisonCaption(comparison?.metrics.avg_rating, 'rating', 'across all platforms'), [comparison]);
  const cmpTotal = useMemo(() => comparisonCaption(comparison?.metrics.total_reviews, 'count', 'all time'), [comparison]);

  const trend = (summary as TrendSummary | undefined)?.avg_rating_trend;
  const overallAvg = useMemo(() => {
    const fromMeta = trend?.meta?.overall_avg;
    if (typeof fromMeta === 'number') return fromMeta;
    if (reviews.length === 0) return null;
    return reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  }, [trend, reviews]);

  const responseRate = useMemo(() => {
    if (reviews.length === 0) return null;
    const replied = reviews.filter((r) => r.reply_text).length;
    return Math.round((replied / reviews.length) * 100);
  }, [reviews]);

  // KPI sparkline series (live rating trend + cumulative volume). LIVE data
  // only -- with fewer than 2 real points, <Spark> renders an empty svg
  // (audit C1: no mock fallback series).
  const ratingSpark = useMemo(() => {
    const s = (trend?.series ?? []).map((p) => p.value).filter((v): v is number => v != null);
    return s.slice(-7);
  }, [trend]);
  const volumeSeries = useMemo(() => (summary as TrendSummary | undefined)?.review_count_by_week?.series ?? [], [summary]);
  const reviewsSpark = useMemo(() => {
    const s = volumeSeries.map((p) => p.value).filter((v): v is number => v != null);
    return s.slice(-7);
  }, [volumeSeries]);

  // Rating-over-time bars (12 weeks). LIVE avg_rating_trend only; when there
  // are not enough real weeks yet the panel says so instead of inventing one.
  const barValues = useMemo(() => {
    const s = (trend?.series ?? []).map((p) => p.value).filter((v): v is number => v != null);
    return s.length >= 4 ? s.slice(-12) : [];
  }, [trend]);

  // Sources (Google-only rule). Real counts only.
  const googleCount = useMemo(() => {
    if (reviews.length) return reviews.filter((r) => r.platform === 'google').length || totalReviews;
    return totalReviews;
  }, [reviews, totalReviews]);

  // "Reply to N reviews" -- unanswered, newest first, top 2 for the hero preview.
  const pending = useMemo(() => reviews.filter((r) => !r.reply_text), [reviews]);
  // DEV-ONLY visual fidelity aid: ?demo=1 seeds the mockup's 2 sample review
  // cards so the hero can be screenshot-compared even when no backend session
  // is minting a data token. Never fires in production (import.meta.env.DEV
  // gate) and never affects live data or actions.
  const demoHero = import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1';
  const heroReviews = useMemo(() => {
    if (pending.length) return pending.slice(0, 2);
    if (!demoHero) return [];
    return [
      { review_id: 'demo-i1', reviewer_name: 'Tom Boyd', color: '#7c4dff', rating: 2, platform: 'google', review_date: new Date(Date.now() - 3 * 86400000).toISOString(), review_text: 'Showed up late and the quote was higher than I expected. Disappointed.', draft_response: "Hi Tom, I'm sorry we ran behind and the estimate caught you off guard. I'd like to make this right and will reach out today.", reply_text: null, draft_id: undefined },
      { review_id: 'demo-i2', reviewer_name: 'Sam Wood', color: '#00897b', rating: 3, platform: 'google', review_date: new Date(Date.now() - 5 * 86400000).toISOString(), review_text: 'Job got done but communication could be better between visits.', draft_response: "Thanks for the honest note, Sam. You're right that we can keep you in the loop better, and we'll tighten that up.", reply_text: null, draft_id: undefined },
    ] as unknown as typeof pending;
  }, [pending, demoHero]);
  const pendingCount = pending.length || (demoHero ? 6 : 0);
  const needsCareCount = useMemo(() => pending.filter((r) => r.rating <= 3).length, [pending]);

  // ---- alerts + funnel + activity ----
  const { data: notifData } = useNotifications({ limit: 50 });
  const alerts = useMemo(() => notifData?.notifications ?? [], [notifData]);
  const { data: requestsData } = useRequestsSummary({ days: 30 });
  const { data: activityData } = useActivity({ days: 364 });

  // All-time requests-sent count (same signal useSetupProgress reads) -- drives
  // the §9 empty-state -> live-tile flip below.
  const { data: requestsMeta } = useRequests({ limit: 1 });
  const requestsSent = requestsMeta?.count ?? 0;

  // ZERO-DATA (brand-new tenant): reviews query has resolved and the account
  // has genuinely no reviews AND no requests sent. Drives the intentional
  // empty-state treatment across the dashboard so it reads as "ready + waiting
  // for data", never as a broken screen. Only true once reviewsData !==
  // undefined so a slow load never flashes empty states at a tenant with data.
  const zeroData =
    !readOnly && reviewsData !== undefined && totalReviews === 0 && requestsSent === 0;

  const heat = useMemo(() => heatFromActivity(activityData?.days ?? []), [activityData]);

  // Momentum: milestone progress toward the next round number of total
  // reviews. REAL total only (no ||42 -- audit C1).
  const momoTotal = totalReviews;
  const momoGoal = nextMilestone(momoTotal);
  const momoRemaining = momoGoal - momoTotal;
  const momoPct = momoGoal > 0 ? Math.round((momoTotal / momoGoal) * 100) : 0;

  // Days since the newest review actually landed (real, from the live list).
  // null = no reviews / not loaded yet.
  const daysSinceLastReview = useMemo(() => {
    let newest: number | null = null;
    for (const r of reviews) {
      const t = Date.parse(r.review_date ?? '');
      if (!Number.isNaN(t) && (newest === null || t > newest)) newest = t;
    }
    if (newest === null) return null;
    return Math.max(0, Math.floor((Date.now() - newest) / 86400000));
  }, [reviews]);

  // Customer words -- REAL excerpts from this tenant's own reviews (audit C1:
  // the prototype's invented quotes are gone). Up to 3 recent 4-5 star quotes
  // ("love") and up to 3 recent 1-3 star quotes ("worries"), attributed to the
  // real reviewer. Columns with nothing real stay honestly empty.
  const customerWords = useMemo(() => {
    const withText = reviews.filter((r) => (r.review_text ?? '').trim().length > 0);
    const excerpt = (t: string) => {
      const s = t.trim().replace(/\s+/g, ' ');
      return s.length > 90 ? s.slice(0, 87).trimEnd() + '...' : s;
    };
    const toQuote = (r: (typeof withText)[number]) => ({
      text: `"${excerpt(r.review_text ?? '')}"`,
      by: `${r.reviewer_name || 'A customer'} - ${r.rating}-star review`,
    });
    return {
      loves: withText.filter((r) => r.rating >= 4).slice(0, 3).map(toQuote),
      worries: withText.filter((r) => r.rating <= 3).slice(0, 3).map(toQuote),
    };
  }, [reviews]);

  const funnelRows = useMemo(() => {
    const f = requestsData?.funnel;
    const sent = f?.sent ?? 0;
    const opened = f?.delivered_or_opened ?? 0;
    const left = f?.left_review ?? 0;
    const pct = (n: number) => (sent > 0 ? Math.round((n / sent) * 100) : 0);
    return [
      { label: 'Requests sent', val: sent, pctTxt: '100%', pct: sent > 0 ? 100 : 0, color: '#1a73e8' },
      { label: 'Delivered or opened', val: opened, pctTxt: `${pct(opened)}%`, pct: pct(opened), color: '#4285F4' },
      { label: 'Left a review', val: left, pctTxt: `${pct(left)}%`, pct: pct(left), color: '#34a853' },
    ];
  }, [requestsData]);

  // Real business name only; no placeholder greeting (audit C1: "Riverside"
  // was the prototype's sample tenant).
  const greetingName = (me?.business_name ?? '').split(' ')[0];
  const advisorLine = zeroData
    ? "Welcome. Finish the quick setup below, then send your first requests. Your ratings, trends, and AI-drafted replies fill in right here as reviews arrive."
    : pendingCount > 0
      ? `Your ${pendingCount} waiting replies are today's win. Clear them and keep your rating on top.`
      : "You're caught up. Send a few requests this week to keep the reviews flowing.";

  // ---- actions (explicit-click only; replies never auto-post) ----
  function handleApprove(id: string) {
    const draftId = draftIdByReview.get(id);
    if (!draftId) return;
    void approve(draftId).catch(() => {});
  }
  function handleSkip(id: string) {
    const draftId = draftIdByReview.get(id);
    if (!draftId) return;
    void skip(draftId).catch(() => {});
  }

  const overallStr = overallAvg != null ? overallAvg.toFixed(1) : '-';
  const overallRound = overallAvg != null ? Math.round(overallAvg) : 0;

  /* ---- HONEST DAY-0 ROUTING (audit C1 + Phase 3 §9) ----
     A tenant whose review data has LOADED and is genuinely empty must never see
     a blank dashboard (a data-less dashboard reads as "broken" -- a top
     onboarding killer). Instead we OVERLAY the unified checklist home (the
     embedded variant of the same derived-completion checklist served at
     /onboarding), so a returning zero-data login lands on the setup home with
     real, derived progress. Only fires once the reviews query has resolved, so
     a slow load never flashes it at a tenant that has data. Skipped in the
     operator read-only tenant view (the checklist hooks are not admin-view-
     aware); there the honest zero-state dashboard renders instead. */
  // §9 EMPTY-STATE (derived entirely from real data):
  //  - requests_sent >= 1 (activated, awaiting first review): clean live counter
  //    tiles (Requests sent / Awaiting / Reviews received) so the owner sees
  //    funnel motion immediately, even before the first review lands.
  //  - requests_sent === 0 (brand-new): the dashboard is now FULLY VIEWABLE with
  //    intentional empty states + the non-blocking FinishSetupCard nudge (below).
  //    It is NO LONGER walled behind a full-screen onboarding checklist
  //    (2026-08-05, Steve): a trial owner must be able to see the product, with
  //    setup one tap away, not hidden behind it. `zeroData` drives the clean
  //    empty-state copy so nothing reads as a broken "-"/blank screen.
  // When the first review arrives (totalReviews > 0) this whole gate is bypassed
  // and the full data dashboard renders.
  if (!readOnly && reviewsData !== undefined && totalReviews === 0 && requestsSent >= 1) {
    return (
      <EmptyStateLiveTiles
        requestsSent={requestsSent}
        reviewsReceived={totalReviews}
        greetingName={greetingName}
        onSendMore={() => navigate('/requests')}
      />
    );
  }

  return (
    <div className="rhf-dash">
      {/* MOBILE greeting + primary-action hero (mockup mobile dashboard head).
          Hidden on desktop where the AppShell topbar carries the greeting. */}
      <div className="rhf-mobile-only">
        <div style={{ marginBottom: 14 }}>
          <div className="rhf-mgreet__h">{daypartGreeting()}{greetingName ? `, ${greetingName}` : ''}</div>
          <div className="rhf-mgreet__s">Here's what needs you today.</div>
        </div>
        <div className="rhf-mhero" data-keepcolor>
          <div className="rhf-mhero__k">{zeroData ? 'Get started' : 'Your #1 job today'}</div>
          <div className="rhf-mhero__big">{zeroData ? 'Finish your setup' : `${pendingCount} replies waiting`}</div>
          <div className="rhf-mhero__sub">
            {zeroData
              ? 'Connect Google and send your first requests.'
              : 'AI drafted them in your voice. Just approve.'}
          </div>
          <button className="rhf-mhero__btn" onClick={() => go(zeroData ? '/onboarding' : '/reviews')}>
            {zeroData ? 'Finish setup →' : 'Review & approve →'}
          </button>
        </div>
      </div>

      {/* ADVISOR LINE */}
      <div className="rhf-advisor">
        <div className="rhf-advisor__icon" data-keepcolor>
          <Bolt />
        </div>
        <div className="rhf-advisor__text">{advisorLine}</div>
      </div>

      {/* FINISH-SETUP CARD (prototype "skippers" nudge). Real-state gated
          inside the component (incomplete setup && reminders not off); hidden
          in the operator read-only tenant view. */}
      {!readOnly && <FinishSetupCard />}

      {/* POST-ACTIVATION reactivation push (spec): dismissible dashboard nudge,
          never a setup-checklist row. Self-gates (activated + not reactivated). */}
      {!readOnly && <ReactivationPush />}

      {/* KPI ROW */}
      <div className="rhf-kpirow">
        <div className="rhf-kpi hue-gold sec-topcap" onClick={() => go('/reviews')}>
          <div className="rhf-kpi__head">
            <div className="rhf-kpi__label">OVERALL RATING</div>
            <CaretR />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
            {totalReviews === 0 ? (
              <span className="rhf-kpi__big" style={{ fontSize: 21, color: 'var(--muted)' }}>
                No reviews yet
              </span>
            ) : (
              <>
                <span className="rhf-kpi__big">{overallStr}</span>
                <Stars n={overallRound} size={16} />
              </>
            )}
          </div>
          <div className="rhf-kpi__row2">
            <span className={'rhf-kpi__delta' + (cmpRating.dir === 'flat' ? ' rhf-kpi__delta--flat' : '')}>
              {cmpRating.dir !== 'flat' && <TrendUp />}
              {cmpRating.caption}
            </span>
            <span className="rhf-kpi__trend">
              <span className="rhf-kpi__trendlbl">7-WK TREND</span>
              <Spark points={ratingSpark} color="#34a853" />
            </span>
          </div>
        </div>

        <div className="rhf-kpi hue-gold sec-topcap" onClick={() => go('/reviews')}>
          <div className="rhf-kpi__head">
            <div className="rhf-kpi__label">TOTAL REVIEWS</div>
            <CaretR />
          </div>
          <div className="rhf-kpi__big" style={{ marginTop: 8 }}>
            {totalReviews.toLocaleString()}
          </div>
          <div className="rhf-kpi__row2">
            <span className={'rhf-kpi__delta' + (cmpTotal.dir === 'flat' ? ' rhf-kpi__delta--flat' : '')}>
              {cmpTotal.dir !== 'flat' && <TrendUp />}
              {cmpTotal.caption}
            </span>
            <span className="rhf-kpi__trend">
              <span className="rhf-kpi__trendlbl">7-WK TREND</span>
              <Spark points={reviewsSpark} color="#1a73e8" />
            </span>
          </div>
        </div>

        <div className="rhf-kpi hue-green sec-topcap" onClick={() => go('/reviews')}>
          <div className="rhf-kpi__head">
            <div className="rhf-kpi__label">RESPONSE RATE</div>
            <CaretR />
          </div>
          <div className="rhf-kpi__big" style={{ marginTop: 8 }}>
            {responseRate != null ? (
              <>
                {responseRate}
                <span className="rhf-kpi__pct">%</span>
              </>
            ) : totalReviews === 0 ? (
              <span style={{ fontSize: 21, fontWeight: 700, color: 'var(--muted)' }}>No reviews yet</span>
            ) : (
              '-'
            )}
          </div>
          <div className="rhf-kpi__row2">
            {/* Real caption + NO fabricated trend series (audit C1: the old
                hardcoded spark shipped to every tenant). A live response-rate
                series can restore the sparkline when the backend supplies one. */}
            <span className="rhf-kpi__delta rhf-kpi__delta--flat">Share of reviews with a reply</span>
          </div>
        </div>

        <div className="rhf-kpi hue-green sec-topcap" onClick={() => go('/automations')}>
          <div className="rhf-kpi__head">
            <div className="rhf-kpi__label">REPLY MODE</div>
            <CaretR />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ fontFamily: 'var(--dmsans)', fontSize: 30, fontWeight: 800, letterSpacing: '-.5px' }}>Manual</span>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34a853' }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: '#41454a' }}>AI drafts, you approve every post</div>
        </div>
      </div>

      {/* TWO COLUMN */}
      <div className="rhf-twocol">
        {/* LEFT */}
        <div className="rhf-col">
          {/* HERO: replies to approve */}
          <div className="rhf-panel rhf-hero">
            <div className="rhf-hero__head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="rhf-hero__badge">
                  {svg({ width: 20, height: 20, fill: 'none', stroke: '#1a73e8', strokeWidth: 2 }, <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.7-.8L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" />)}
                </div>
                <div>
                  <div className="rhf-hero__title">
                    Reply to <span style={{ color: '#1a73e8' }}>{pendingCount}</span> reviews
                  </div>
                  <div className="rhf-hero__sub">AI drafted these in your voice. Approve, edit, or skip.</div>
                </div>
              </div>
              <div className="rhf-link" onClick={() => go('/reviews')}>
                Open inbox →
              </div>
            </div>

            {heroReviews.length > 0 ? (
              <div>
                {heroReviews.map((r) => {
                  const name = r.reviewer_name || 'Anonymous';
                  const care = r.rating <= 3;
                  const avColor = (r as { color?: string }).color ?? avatarColor(name);
                  return (
                    <div className="rhf-review" key={r.review_id}>
                      <div className="rhf-review__row">
                        <div className="rhf-avatar" style={{ background: avColor }}>
                          {name[0]?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="rhf-review__meta">
                            <span className="rhf-review__name">{name}</span>
                            <Stars n={r.rating} size={15} />
                            {care && <span className="rhf-badge-care">NEEDS CARE</span>}
                            <span className="rhf-review__src">
                              <GoogleG /> Google · {relativeTime(r.review_date)}
                            </span>
                          </div>
                          <p className="rhf-review__text">{r.review_text}</p>
                          {(r.draft_response ?? r.reply_text) && (
                            <div className="rhf-draft">
                              <div className="rhf-draft__label">
                                {svg({ width: 13, height: 13, fill: '#1a73e8' }, <path d="M12 2l2 6 6 .5-4.6 4 1.4 6L12 15.8 7.2 18.5l1.4-6L4 8.5 10 8z" />)}
                                AI DRAFT · IN YOUR VOICE
                              </div>
                              <p className="rhf-draft__text">{r.draft_response ?? r.reply_text}</p>
                            </div>
                          )}
                          {!readOnly && (
                            <div className="rhf-actions">
                              <button className="rhf-btn rhf-btn--primary" onClick={() => handleApprove(r.review_id)}>
                                {svg({ width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 3 }, <path d="M20 6L9 17l-5-5" />)}
                                Approve &amp; post
                              </button>
                              <button className="rhf-btn rhf-btn--ghost" onClick={() => navigate('/reviews')}>
                                Edit
                              </button>
                              <button className="rhf-btn rhf-btn--text" onClick={() => handleSkip(r.review_id)}>
                                Skip
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="rhf-hero__foot">
                  <span className="rhf-link" onClick={() => go('/reviews')}>
                    View all {pendingCount} in your inbox →
                  </span>
                </div>
              </div>
            ) : (
              <div className="rhf-empty">
                <div className="rhf-empty__star">{svg({ width: 30, height: 30, fill: '#fbbc04' }, STAR)}</div>
                <div className="rhf-empty__title">
                  {zeroData ? 'Your reviews will appear here' : 'Inbox zero. Nice work'}
                </div>
                <div className="rhf-empty__sub">
                  {zeroData
                    ? 'Connect Google and send your first requests. New reviews and their AI-drafted replies land right here.'
                    : 'Every review has a reply. Keep the momentum going.'}
                </div>
                <button className="rhf-btn rhf-btn--primary" style={{ padding: '14px 26px', fontSize: 17 }} onClick={() => go('/requests')}>
                  {svg({ width: 16, height: 16, fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 }, <path d="M12 5v14M5 12h14" />)}
                  {zeroData ? 'Send your first requests' : 'Request more reviews'}
                </button>
              </div>
            )}
          </div>

          {/* REVIEW ACTIVITY HEATMAP */}
          <div className="rhf-panel rhf-panel--pad hue-gold sec-topcap">
            <div className="rhf-heat__headrow">
              <div className="rhf-panel__title">Review activity</div>
              <div className="rhf-panel__sub">Last 6 months</div>
            </div>
            <div className="rhf-heat__desc">Each square is a day. Darker means more reviews landed.</div>
            {/* The heatmap is ~449px of day squares and cannot shrink. It
                scrolls inside its OWN box so it never widens the page on a
                phone (see the min-width:0 note in dashboard-faithful.css). */}
            <div className="rhf-heat__scroll">
              <Heatmap weeks={heat.weeks} total={heat.total} />
            </div>
          </div>

          {/* CUSTOMER LANGUAGE */}
          <div className="rhf-panel hue-green sec-topcap" style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="rhf-panel__title">Your customers' own words</div>
              <div className="rhf-panel__sub">Pulled from your reviews</div>
            </div>
            <p style={{ margin: '6px 0 18px', fontSize: 15, fontWeight: 600, color: '#41454a' }}>
              Real phrases to lean on in your marketing, and worries to get ahead of.
            </p>
            {/* REAL excerpts from this tenant's own reviews (audit C1: the
                prototype's invented quotes + fake "N reviews mention this"
                counts are gone). Empty columns say so honestly. */}
            <div className="rhf-words__grid">
              <div>
                {/* Label ink is the DEEP green (#0f5c25), not the brand green
                    (#34a853): green-on-white measured 3.00:1 rendered, which
                    fails AA. #0f5c25 is the AAA green tokens.css already
                    blesses -- 8.00:1, and it still reads green. The DOT keeps
                    the vivid brand green; it is decoration, not text. */}
                <div className="rhf-words__label" style={{ color: '#0f5c25' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34a853' }} /> WHAT THEY LOVE
                </div>
                <div className="rhf-words__col">
                  {customerWords.loves.length > 0 ? (
                    customerWords.loves.map((q, i) => (
                      <div className="rhf-quote" key={i} style={{ borderLeft: '3px solid #34a853' }}>
                        <div className="rhf-quote__text">{q.text}</div>
                        <div className="rhf-quote__by">{q.by}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#41454a', padding: '8px 0' }}>
                      4-5 star review quotes will show up here.
                    </div>
                  )}
                </div>
              </div>
              <div>
                {/* Same treatment as the green label: #ea8600 amber measured
                    2.59:1 on white, the worst text pair on this screen.
                    #8a4600 is 6.95:1 and still reads amber. The DOT keeps the
                    vivid brand gold. */}
                <div className="rhf-words__label" style={{ color: '#8a4600' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbc04' }} /> WHAT WORRIES THEM
                </div>
                <div className="rhf-words__col">
                  {customerWords.worries.length > 0 ? (
                    customerWords.worries.map((q, i) => (
                      <div className="rhf-quote" key={i} style={{ borderLeft: '3px solid #fbbc04' }}>
                        <div className="rhf-quote__text">{q.text}</div>
                        <div className="rhf-quote__by">{q.by}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#41454a', padding: '8px 0' }}>
                      No 1-3 star reviews right now. Concerns to get ahead of will show up here.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="rhf-col">
          {/* MOMENTUM -- real signals only (audit C1: the fake streak, the
              hardcoded "no new review in 3 days" alert, and the invented
              "best week: 38 replies" line are gone). Shows a REAL send-a-
              request nudge when the newest review is genuinely 3+ days old,
              the real recency line, and the real milestone progress bar. */}
          <div className="rhf-panel rhf-panel--pad hue-gold sec-topcap">
            <div className="rhf-panel__title" style={{ marginBottom: 14 }}>Your momentum</div>
            {daysSinceLastReview != null && daysSinceLastReview >= 3 && (
              <div className="rhf-mom__alert">
                {/* Icon stroke deepened to #8a4600 (Brad, 2026-08-06). Non-text contrast
                    (WCAG 1.4.11) needs 3:1; #ea8600 measured 2.59:1 on this white
                    panel. #8a4600 is 6.95:1 and still reads amber-warning. */}
                {svg({ width: 20, height: 20, fill: 'none', stroke: '#8a4600', strokeWidth: 2, style: { flexShrink: 0, marginTop: 1 } }, <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>)}
                <div style={{ flex: 1 }}>
                  <div className="rhf-mom__alert-title">Keep the reviews coming</div>
                  <div className="rhf-mom__alert-sub">
                    No new review in {daysSinceLastReview} days. Send a request to keep the momentum going.
                  </div>
                  {!readOnly && (
                    <button className="rhf-btn rhf-btn--primary" style={{ marginTop: 10, padding: '9px 16px' }} onClick={() => navigate('/requests')}>
                      Send a request
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="rhf-mom__streak">
              <div className="rhf-mom__medal" data-keepcolor>
                {svg({ width: 24, height: 24, fill: '#fbbc04' }, STAR)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontFamily: 'var(--dmsans)', fontWeight: 800, fontSize: 17, color: '#5c3b00' }}>
                    {momoTotal.toLocaleString()} review{momoTotal === 1 ? '' : 's'} all time
                  </span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#5c3b00', marginTop: 3 }}>
                  {daysSinceLastReview == null
                    ? 'Newest review recency will show here.'
                    : daysSinceLastReview === 0
                      ? 'Newest review landed today.'
                      : daysSinceLastReview === 1
                        ? 'Newest review landed yesterday.'
                        : `Newest review landed ${daysSinceLastReview} days ago.`}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="rhf-mom__bar-lbl">
                <span>
                  {momoRemaining} more to reach <strong style={{ color: '#b06000' }}>{momoGoal}</strong> 🏆
                </span>
                <span style={{ fontWeight: 800, color: '#000000' }}>
                  {momoTotal} / {momoGoal}
                </span>
              </div>
              <div style={{ height: 8, background: '#eef0f3', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${momoPct}%`, height: '100%', background: 'linear-gradient(90deg,#fbbc04,#f5a623)', borderRadius: 999 }} />
              </div>
            </div>
          </div>

          {/* RATING OVER TIME */}
          <div className="rhf-panel rhf-panel--pad hue-gold sec-topcap">
            <div className="rhf-bars__head">
              <div>
                <div className="rhf-panel__title">Rating over time</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#41454a', marginTop: 3 }}>Weekly average Google star rating</div>
              </div>
              <div className="rhf-bars__scale">
                Last 12 weeks
                <div style={{ fontSize: 14, fontWeight: 700 }}>Scale 3.8-5.0★</div>
              </div>
            </div>
            {barValues.length > 0 ? (
              <RatingBars values={barValues} />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 600, color: '#41454a', padding: '18px 0' }}>
                Not enough weeks of review data yet. The trend chart fills in as reviews arrive.
              </div>
            )}
          </div>

          {/* WHERE FROM -- Google only. Review ingestion has been Google-only
              since 2026-06-28 (monitor_agent hard-refuses yelp + facebook), so
              this panel names one source and does not imply others. */}
          <div className="rhf-panel rhf-panel--pad hue-blue sec-topcap">
            <div className="rhf-panel__title" style={{ marginBottom: 14 }}>Where reviews come from</div>
            {googleCount > 0 ? (
              <>
                <div className="rhf-src__row">
                  <div className="rhf-src__name">
                    <GoogleG s={18} /> Google
                  </div>
                  <div className="rhf-src__count">{googleCount}</div>
                </div>
                <div className="rhf-src__track">
                  <div style={{ width: '100%', height: '100%', background: '#4285F4', borderRadius: 999 }} />
                </div>
                <div className="rhf-src__foot">All {googleCount} reviews are from Google, your only connected review source.</div>
              </>
            ) : (
              /* Honest empty state. "All 0 reviews are from Google" reads as a
                 broken stat on a day-0 account. */
              <div className="rhf-src__foot" style={{ marginTop: 0 }}>
                No reviews yet. Google is your connected review source, and new Google reviews show up here.
              </div>
            )}
          </div>

          {/* NEEDS ATTENTION */}
          <div className="rhf-panel rhf-panel--pad hue-red sec-topcap">
            <div className="rhf-panel__title" style={{ marginBottom: 12 }}>Needs attention</div>
            <div className="rhf-attn">
              {alerts.length > 0 ? (
                alerts.slice(0, 3).map((a) => (
                  <div className="rhf-attn__row" key={a.id}>
                    <div className="rhf-attn__ic" style={{ background: a.kind === 'warn' ? '#fce8e6' : a.kind === 'win' ? '#e6f4ea' : '#e8f0fe' }}>
                      {svg({ width: 14, height: 14, fill: 'none', stroke: a.kind === 'warn' ? '#d93025' : a.kind === 'win' ? '#188038' : '#1a73e8', strokeWidth: 2.5 }, <><circle cx="12" cy="12" r="9" /><path d="M12 8v.01M11 12h1v4h1" /></>)}
                    </div>
                    <div>
                      <div className="rhf-attn__title">{a.title}</div>
                      <div className="rhf-attn__sub">{a.body}</div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="rhf-attn__row">
                    <div className="rhf-attn__ic" style={{ background: '#fce8e6' }}>
                      {svg({ width: 14, height: 14, fill: 'none', stroke: '#d93025', strokeWidth: 2.5 }, <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>)}
                    </div>
                    <div>
                      <div className="rhf-attn__title">
                        {needsCareCount > 0 ? `${needsCareCount} low-star review${needsCareCount === 1 ? '' : 's'} need a reply` : 'Low-star reviews get a personal reply'}
                      </div>
                      <div className="rhf-attn__sub">These deserve a personal reply, never a bulk one.</div>
                    </div>
                  </div>
                  <div className="rhf-attn__row">
                    <div className="rhf-attn__ic" style={{ background: '#e8f0fe' }}>
                      {svg({ width: 14, height: 14, fill: 'none', stroke: '#1a73e8', strokeWidth: 2.5 }, <><circle cx="12" cy="12" r="9" /><path d="M12 8v.01M11 12h1v4h1" /></>)}
                    </div>
                    <div>
                      <div className="rhf-attn__title">
                        {funnelRows[0].val > 0 ? `${funnelRows[0].val} requests sent this month` : 'No requests sent yet this month'}
                      </div>
                      <div className="rhf-attn__sub">
                        {funnelRows[0].val > 0
                          ? `${funnelRows[2].val} customer${funnelRows[2].val === 1 ? '' : 's'} left a review.`
                          : 'Send a few to keep reviews flowing.'}
                      </div>
                    </div>
                  </div>
                  <div className="rhf-attn__row">
                    <div className="rhf-attn__ic" style={{ background: '#f1f3f4' }}>
                      {svg({ width: 14, height: 14, fill: 'none', stroke: '#41454a', strokeWidth: 2.5 }, <><circle cx="12" cy="12" r="9" /><path d="M5 5l14 14" /></>)}
                    </div>
                    <div>
                      <div className="rhf-attn__title">Do-not-contact is respected</div>
                      <div className="rhf-attn__sub">Flagged customers are skipped automatically.</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* THIS MONTH'S REQUESTS */}
          <div className="rhf-panel rhf-panel--pad hue-blue sec-topcap">
            <div className="rhf-panel__title" style={{ marginBottom: 16 }}>This month's requests</div>
            <div className="rhf-funnel">
              {funnelRows.map((f) => (
                <div key={f.label}>
                  <div className="rhf-funnel__lbl">
                    <span>{f.label}</span>
                    <b>
                      {f.val} · {f.pctTxt}
                    </b>
                  </div>
                  <div className="rhf-funnel__track">
                    <div className="rhf-funnel__fill" style={{ width: `${f.pct}%`, background: f.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The APPEARANCE toggle panel that used to close this column is
              RETIRED (Steve, 2026-08-06). The app is outdoor high-contrast,
              always. */}
        </div>
      </div>
    </div>
  );
}
